import React, { useState, useEffect } from 'react';
import {
    Grid, CircularProgress, MenuItem, Select, Typography, Box
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { variables } from '../Variables';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/en'; // or your locale

dayjs.extend(weekday);
dayjs.extend(localizedFormat);
dayjs.locale('en');

const defaultFilter = {
    PageNo: 1,
    PageSize: 10,
    OrderBy: '',
    OrderType: '',
    OrderId: null,
    UserId: null,
    ShippingMethodId: null,
    OrderStatusId: null,
    CreatedOn: null,
    UpdatedOn: null
};

export const Orders = ({ showMessage }) => {
    const user = useSelector((state) => state.user.user);
    const jsonconfig = {
        headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json'
        }
    };

    const [orders, setOrders] = useState([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(defaultFilter);
    const [orderStatuses, setOrderStatuses] = useState([]);

    const loadOrderStatuses = async () => {
        try {
            const res = await axios.get(`${variables.API_URL}Order/GetOrderStatuses`);
            setOrderStatuses(res.data.orderStatuses || []);
        } catch (error) {
            showMessage('error', 'Failed to load order statuses.');
        }
    };


    const loadOrders = () => {
        setLoading(true);
        axios.post(
            `${variables.API_URL}Order/GetAllOrdersForAdmin`,
            filter,
            jsonconfig
        ).then(res => {
            setOrders(res.data.orders || []);
            setTotalOrders(res.data.totalCount || 0);
        }).catch(() => {
            showMessage('error', 'Failed to load orders.');
        }).finally(() => setLoading(false));
    };

    const handleStatusChange = async (orderId, newStatusId) => {
        try {
            const payload = {
                orderId: orderId,          // string
                orderStatusId: newStatusId // int
            };

            const response = await axios.post(
                `${variables.API_URL}Order/UpdateOrderStatus`,
                JSON.stringify(payload),
                jsonconfig
            );

            if (response.status === 200 && response.data?.message) {
                setOrders(prev =>
                    prev.map(order =>
                        order.orderId === orderId
                            ? { ...order, orderStatusId: newStatusId }
                            : order
                    )
                );
                showMessage('success', response.data.message);
            } else {
                showMessage('error', response.data?.message || 'Unexpected response');
            }
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || 'Failed to update order status.';
            showMessage('error', errorMessage);
        }
    };


    const columns = [
        { field: 'orderId', headerName: 'Order ID', width: 100 },
        { field: 'userId', headerName: 'User ID', width: 100 },
        { field: 'subTotal', headerName: 'Subtotal', width: 100 },
        { field: 'discount', headerName: 'Discount', width: 100 },
        { field: 'vat', headerName: 'VAT', width: 100 },
        { field: 'total', headerName: 'Total', width: 100 },
        {
            field: 'orderStatusId',
            headerName: 'Status',
            width: 180,
            renderCell: (params) => (
                <Select
                    size="small"
                    value={params.row.orderStatusId}
                    onChange={(e) =>
                        handleStatusChange(params.row.orderId, parseInt(e.target.value))
                    }
                >
                    {orderStatuses.map((status) => (
                        <MenuItem key={status.orderStatusId} value={status.orderStatusId}>
                            {status.statusName}
                        </MenuItem>
                    ))}
                </Select>
            )
        },
        {
            field: 'createdOn', headerName: 'Created On',
            valueFormatter: (params) =>
                dayjs(params.value).format('ddd-MMM-YYYY HH:mm'),
            width: 180
        },
        {
            field: 'updatedOn', headerName: 'Updated On',
            valueFormatter: (params) =>
                dayjs(params.value).format('ddd-MMM-YYYY HH:mm'),
            width: 180
        }
    ];

    const handleFilterChange = (filterModel) => {
        const newFilter = {
            ...filter,
            OrderId: null,
            UserId: null,
            ShippingMethodId: null,
            OrderStatusId: null,
            CreatedOn: null,
            UpdatedOn: null,
            PageNo: 1
        };

        const activeFilters = filterModel.items.filter(
            (item) => item.value !== undefined && item.value !== null && item.value !== ''
        );

        activeFilters.forEach(({ field, value }) => {
            if (field in newFilter) {
                newFilter[field] = isNaN(value) ? value : parseInt(value);
            }
        });

        if (JSON.stringify(filter) !== JSON.stringify(newFilter)) {
            setFilter(newFilter);
        }
    };

    const renderOrderItems = (order) => (
        <Box sx={{ pl: 2 }}>
            <Typography variant="subtitle2">Order Items</Typography>
            <ul style={{ marginTop: 8 }}>
                {order.orderItems?.map(item => (
                    <li key={item.orderItemId}>
                        {item.productName} - {item.quantity} x {item.unitPrice}
                    </li>
                ))}
            </ul>
        </Box>
    );

    useEffect(() => {
        loadOrderStatuses();
    }, []);

    useEffect(() => {
        loadOrders();
    }, [
        filter.PageNo,
        filter.PageSize,
        filter.OrderId,
        filter.UserId,
        filter.ShippingMethodId,
        filter.OrderStatusId,
        filter.OrderBy,
        filter.OrderType,
        filter.CreatedOn,
        filter.UpdatedOn
    ]);

    return (
        <div className="row">
            <div className="col-sm-12 px-4">
                <span className="form-title">Orders List</span>
                <div className="form-container mb-3" style={{ height: 600, width: '100%' }}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <DataGrid
                            rows={orders}
                            columns={columns}
                            getRowId={(row) => row.orderId}
                            rowCount={totalOrders}
                            loading={loading}
                            paginationMode="server"
                            sortingMode="server"
                            filterMode="server"
                            onFilterModelChange={handleFilterChange}
                            paginationModel={{
                                page: filter.PageNo - 1,
                                pageSize: filter.PageSize,
                            }}
                            onPaginationModelChange={({ page, pageSize }) => {
                                setFilter(prev => ({
                                    ...prev,
                                    PageNo: page + 1,
                                    PageSize: pageSize,
                                }));
                            }}
                            sortModel={
                                filter.OrderBy && filter.OrderType
                                    ? [{ field: filter.OrderBy, sort: filter.OrderType }]
                                    : []
                            }
                            onSortModelChange={(sortModel) => {
                                if (sortModel.length > 0 && (sortModel[0].sort === 'asc' || sortModel[0].sort === 'desc')) {
                                    const { field, sort } = sortModel[0];
                                    setFilter(prev => ({
                                        ...prev,
                                        OrderBy: field,
                                        OrderType: sort
                                    }));
                                } else {
                                    setFilter(prev => ({
                                        ...prev,
                                        OrderBy: '',
                                        OrderType: ''
                                    }));
                                }
                            }}
                            pageSizeOptions={[10, 20, 50]}
                            // ✅ Required for expandable rows
                            getDetailPanelContent={({ row }) => renderOrderItems(row)}
                            getDetailPanelHeight={() => 'auto'}
                            // ✅ Optional: adds expand/collapse icon column
                            getRowHeight={() => 'auto'}
                            sx={{
                                '& .MuiDataGrid-detailPanelToggleCell': {
                                    flex: '0 0 30px',
                                },
                            }}
                        />

                    )}
                </div>
            </div>
        </div>
    );
};
